import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import dayjs from "dayjs";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ChannelEnum } from "../../constants/enums";
import { formatMessageContent } from "../../utils/convert";

// ---------------------------------------------------------------------------
// A faithful, self-contained recreation of a WhatsApp conversation thread —
// used to show reviewers exactly what a recipient sees, including the
// automated OK -> Link and STOP -> UnsubscribeLink reply flow. The OK/STOP
// turns are not captured transcript, they're simulated from the campaign's
// configured link/unsubscribe fields, so the disclaimer under the phone is
// load-bearing, not decorative. Styling lives in app/globals.css under the
// `wa-chat-` prefix (see that file's WhatsAppChatPreview section).
// ---------------------------------------------------------------------------

const ROW_STAGGER_SECONDS = 0.35;
const CHECK_POP_DELAY_SECONDS = 0.3;
const OK_REPLY_GAP_MINUTES = 2;
const STOP_REPLY_GAP_MINUTES = 6;

type ChatSide = "in" | "out";

type ChatMessage = {
  id: string;
  side: ChatSide;
  /** Sanitized HTML — used for real message content and generated replies. */
  html?: string;
  /** Plain text — used for the simulated OK/STOP taps. */
  text?: string;
  image?: string | null;
  time: string;
};

const buildConversation = (params: {
  content?: string | null;
  image?: string | null;
  channel?: ChannelEnum | number | null;
  link?: string | null;
  unsubscribeLink?: string | null;
  /** Full OK-reply copy, Link already formatted inline — used as-is when
   * present. Falls back to a generic templated reply when it isn't (older
   * records without CopyTextOk). */
  copyTextOk?: string | null;
  copyTextUnsub?: string | null;
  sendDate?: string | Date | null;
}): ChatMessage[] => {
  const {
    content,
    image,
    channel,
    link,
    unsubscribeLink,
    copyTextOk,
    copyTextUnsub,
    sendDate,
  } = params;
  // Same -7h display convention used everywhere else in the app (see
  // DetailCampaign.tsx / CampaignMemberLogTable.tsx / HistoryCampaignCardContent.tsx).
  const base = sendDate ? dayjs(sendDate).add(-7, "hour") : dayjs();
  const messages: ChatMessage[] = [];

  if (content) {
    messages.push({
      id: "campaign-message",
      side: "out",
      html: DOMPurify.sanitize(formatMessageContent(content, channel)),
      image,
      time: base.format("HH:mm"),
    });
  }

  if (link || copyTextOk) {
    const time = base.add(OK_REPLY_GAP_MINUTES, "minute").format("HH:mm");
    const html = copyTextOk
      ? DOMPurify.sanitize(formatMessageContent(copyTextOk, channel))
      : `Great news! Here's your membership login link 👇<br /><a href="${link}" target="_blank" rel="noopener noreferrer">${link}</a>`;
    messages.push(
      { id: "reply-ok", side: "in", text: "OK", time },
      { id: "reply-ok-response", side: "out", html, time },
    );
  }

  if (unsubscribeLink || copyTextUnsub) {
    const time = base.add(STOP_REPLY_GAP_MINUTES, "minute").format("HH:mm");
    const html = copyTextUnsub
      ? DOMPurify.sanitize(formatMessageContent(copyTextUnsub, channel))
      : `You're one tap away from opting out. Confirm below to stop WhatsApp messages:<br /><a href="${unsubscribeLink}" target="_blank" rel="noopener noreferrer">${unsubscribeLink}</a>`;
    messages.push(
      { id: "reply-stop", side: "in", text: "STOP", time },
      { id: "reply-stop-response", side: "out", html, time },
    );
  }

  return messages;
};

const DoubleCheck: React.FC<{ read?: boolean; delaySeconds: number }> = ({
  read = true,
  delaySeconds,
}) => (
  <svg
    className="wa-chat-check-icon"
    width="16"
    height="11"
    viewBox="0 0 16 11"
    fill="none"
    style={{ animationDelay: `${delaySeconds}s` }}>
    <path
      d="M1 5.5L4.5 9L11 1.5"
      stroke={read ? "#53bdeb" : "#8696a0"}
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 5.5L8.5 9L15 1.5"
      stroke={read ? "#53bdeb" : "#8696a0"}
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MessageBubble: React.FC<{ message: ChatMessage; index: number }> = ({
  message: m,
  index,
}) => {
  const rowDelay = index * ROW_STAGGER_SECONDS;
  const hasImage = !!m.image;

  return (
    <div
      className={`wa-chat-row wa-chat-row--${m.side}`}
      style={{ animationDelay: `${rowDelay}s` }}>
      <div
        className={`wa-chat-bubble wa-chat-bubble--${m.side}${hasImage ? " wa-chat-bubble--media" : ""}`}>
        {hasImage && (
          <img
            className="wa-chat-media-img"
            src={m.image!}
            alt="Message attachment"
          />
        )}
        <div
          className={hasImage ? "wa-chat-bubble-content--media" : undefined}>
          {m.html ? (
            <div
              className="wa-chat-rich-text"
              dangerouslySetInnerHTML={{ __html: m.html }}
            />
          ) : (
            m.text
          )}
          <div className="wa-chat-meta">
            {m.time}
            {m.side === "out" && (
              <DoubleCheck delaySeconds={rowDelay + CHECK_POP_DELAY_SECONDS} />
            )}
          </div>
          <div className="wa-chat-clear" />
        </div>
      </div>
    </div>
  );
};

export type WhatsAppChatPreviewProps = {
  /** Raw message body, e.g. CampaignMemberLog.Content */
  content?: string | null;
  image?: string | null;
  /** Sent when the member replies OK — membership login link */
  link?: string | null;
  /** Sent when the member replies STOP — opt-out confirmation link */
  unsubscribeLink?: string | null;
  /** Full OK-reply copy (CampaignMemberLog.CopyTextOk) — Link is already
   * formatted inline, so this is preferred over templating `link` ourselves. */
  copyTextOk?: string | null;
  /** Full STOP-reply copy (CampaignMemberLog.CopyTextUnsub) */
  copyTextUnsub?: string | null;
  channel?: ChannelEnum | number | null;
  /** The recipient this thread is with — shown in the chat header, same as
   * WhatsApp shows an unsaved contact's name (+ number as subtitle). */
  contactName?: string;
  contactPhone?: string;
  /** Anchors the thread's timestamps; defaults to now */
  sendDate?: string | Date | null;
};

const WhatsAppChatPreview: React.FC<WhatsAppChatPreviewProps> = ({
  content,
  image,
  link,
  unsubscribeLink,
  copyTextOk,
  copyTextUnsub,
  channel,
  contactName = "Member",
  contactPhone,
  sendDate,
}) => {
  const messages = useMemo(
    () =>
      buildConversation({
        content,
        image,
        channel,
        link,
        unsubscribeLink,
        copyTextOk,
        copyTextUnsub,
        sendDate,
      }),
    [
      content,
      image,
      channel,
      link,
      unsubscribeLink,
      copyTextOk,
      copyTextUnsub,
      sendDate,
    ],
  );

  const dateLabel = useMemo(
    () =>
      sendDate ? dayjs(sendDate).add(-7, "hour").format("DD MMM YYYY") : "Today",
    [sendDate],
  );

  const initial = contactName.trim().charAt(0).toUpperCase() || "M";
  const isSimulatedFlow = !!(link || unsubscribeLink || copyTextOk || copyTextUnsub);

  return (
    <div>
      <div className="wa-chat-shell">
        <div className="wa-chat-header">
          <div className="wa-chat-avatar">{initial}</div>
          <div className="wa-chat-header-text">
            <div className="wa-chat-header-name">{contactName}</div>
            <div className="wa-chat-header-status">
              {contactPhone || "Campaign recipient"}
            </div>
          </div>
          <div className="wa-chat-header-icons">
            <span>📷</span>
            <span>📞</span>
            <span>⋮</span>
          </div>
        </div>

        <div className="wa-chat-body">
          <div className="wa-chat-date-chip">{dateLabel}</div>

          {messages.length === 0 ? (
            <div className="wa-chat-empty">No message content available.</div>
          ) : (
            messages.map((m, index) => (
              <MessageBubble key={m.id} message={m} index={index} />
            ))
          )}
        </div>

        <div className="wa-chat-input-bar">
          <div className="wa-chat-input-pill">Message</div>
          <div className="wa-chat-mic-btn">🎤</div>
        </div>
      </div>

      {isSimulatedFlow && (
        <p className="wa-chat-disclaimer">
          <InfoCircleOutlined style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            The <strong>OK</strong> and <strong>STOP</strong> replies above are
            simulated to preview your automated link/unsubscribe flow — not a
            captured conversation.
          </span>
        </p>
      )}
    </div>
  );
};

export default WhatsAppChatPreview;
